import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  alpha,
} from '@mui/material';
import { Folder, Description } from '@mui/icons-material';
import BaseTab from './BaseTab';

interface FrontendStructureTabProps {
  isDark: boolean;
  colors: any;
  discordFont: any;
  glassStyle: any;
  lokiGradient: string;
  goldColor: string;
  isMobile: boolean;
}

const FrontendStructureTab: React.FC<FrontendStructureTabProps> = (props) => {
  const getColor = (index: number) => {
    const colors = [props.goldColor, '#0F3B2E', '#1D5C4A', '#051814'];
    return colors[index % colors.length];
  };

  return (
    <BaseTab
      {...props}
      title="Frontend Project Structure"
      emoji="📁"
    >
      <Typography variant="h6" sx={{ color: props.goldColor, mb: 3 }}>
        Organized Codebase Structure
      </Typography>
      
      <Paper sx={{ bgcolor: alpha('#051814', 0.8), p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="body2" fontFamily="monospace" color={props.goldColor} sx={{ lineHeight: 1.8 }}>
          WFHCafe/<br />
          ├── public/                    # Static assets<br />
          │   ├── favicon.ico<br />
          │   └── robots.txt<br />
          ├── src/<br />
          │   ├── assets/               # Images, icons, fonts<br />
          │   ├── components/           # Reusable UI components<br />
          │   │   ├── Admin/           # Admin-specific components<br />
          │   │   ├── Chat/            # Chat system components<br />
          │   │   ├── community/       # Community features<br />
          │   │   ├── communityDetail/ # Community detail views<br />
          │   │   ├── Explore/         # Café discovery<br />
          │   │   ├── LandingPage/     # Landing page sections<br />
          │   │   └── Profile/         # Profile components<br />
          │   ├── context/             # React Context providers<br />
          │   │   ├── AuthContext.tsx<br />
          │   │   └── ThemeContext.tsx<br />
          │   ├── pages/               # Route-based page components<br />
          │   ├── services/            # API service layer<br />
          │   ├── types/               # TypeScript type definitions<br />
          │   ├── utils/               # Utility functions<br />
          │   ├── App.tsx              # Main application component<br />
          │   ├── main.tsx             # Application entry point<br />
          │   └── vite-env.d.ts        # Vite environment types<br />
          ├── .env.example             # Environment variables template<br />
          ├── eslint.config.js         # ESLint configuration<br />
          ├── index.html               # HTML template<br />
          ├── package.json             # Dependencies and scripts<br />
          ├── tailwind.config.js       # Tailwind CSS configuration<br />
          ├── tsconfig.json            # TypeScript configuration<br />
          ├── vite.config.ts           # Vite build configuration<br />
          └── README.md
        </Typography>
      </Paper>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ bgcolor: alpha(props.goldColor, 0.05) }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, color: props.goldColor }}>
                Key Directories
              </Typography>
              <List>
                {[
                  'components/ - Reusable UI components',
                  'pages/ - Route-based page components',
                  'services/ - API service layer',
                  'context/ - State management',
                  'types/ - TypeScript definitions',
                ].map((dir, idx) => (
                  <ListItem key={idx} sx={{ py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Folder sx={{ color: getColor(idx) }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={dir} 
                      primaryTypographyProps={{ color: props.colors.textPrimary }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ bgcolor: alpha('#0F3B2E', 0.05) }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, color: '#0F3B2E' }}>
                Configuration Files
              </Typography>
              <List>
                {[
                  'vite.config.ts - Build configuration',
                  'tsconfig.json - TypeScript config',
                  'tailwind.config.js - Styling config',
                  '.env.example - Environment template',
                  'eslint.config.js - Code quality',
                ].map((file, idx) => (
                  <ListItem key={idx} sx={{ py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Description sx={{ color: getColor(idx + 2) }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={file} 
                      primaryTypographyProps={{ color: props.colors.textPrimary }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </BaseTab>
  );
};

export default FrontendStructureTab;